'use client';

import { useRef, useState, useCallback, useMemo } from 'react';
import { Condition, useCallbackRef } from '@skin-seoul/utils';
import { CLASSNAME, useFiboService } from '@skin-seoul/service';
import { createFiboCollection } from '@/components/fibosearch/FibosearchCombobox/utils';
import { useOutsideClickEffect } from '@skin-seoul/utils';
import { FibosearchCombobox } from '@/components/fibosearch/FibosearchCombobox/FibosearchCombobox';
import { CollectionGroup } from '@/components/fibosearch/FibosearchCombobox/CollectionGroup';
import { CollectionList } from '@/components/fibosearch/FibosearchCombobox/CollectionList';
import HeaderInput from './HeaderInput';
import { useSafeRouter } from '@/hooks/useSafeRouter';
import type { FiboCollectionComboboxItem } from '@skin-seoul/service';
import { Flex } from '@skin-seoul/design-system';
import { debounce } from 'es-toolkit';
const HeaderComboboxSearchInput = () => {
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef(null);
  const [comboboxContentRef, setComboboxContentRef] = useState<HTMLDivElement | null>(null);
  const router = useSafeRouter();
  const {
    useFiboSearch
  } = useFiboService();
  const {
    data: fiboSearchData
  } = useFiboSearch({
    s: inputValue
  });
  const handleDebounceInputChange = useCallbackRef((details: {
    inputValue: string;
  }) => {
    const debouncedFn = debounce((value: string) => {
      setInputValue(value);
    }, 200);
    debouncedFn(details.inputValue);
    return () => {
      debouncedFn.cancel();
    };
  });
  const handleInputClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(true);
  }, []);
  const handleInputFocus = useCallback(() => {
    setIsOpen(true);
  }, []);
  const suggestions = useMemo(() => fiboSearchData?.suggestions ?? [], [fiboSearchData]);
  const {
    collection,
    groupedCollection,
    isGroupedCollection,
    convertedCollectionComboboxItems
  } = useMemo(() => createFiboCollection(suggestions), [suggestions]);
  const shouldDisplayContent = inputValue.length > 0 && isOpen && suggestions.length > 0;
  const handleNavigate = useCallback((url: string) => {
    router.push(url);
  }, [router]);
  const handleSearch = () => {
    handleNavigate(`/search/?s=${encodeURIComponent(inputValue)}`);
    setIsOpen(false);
  };
  useOutsideClickEffect(comboboxContentRef, () => {
    if (document.activeElement !== inputRef.current) {
      setIsOpen(false);
    }
  });
  return <FibosearchCombobox.Root collection={collection} loopFocus onInputValueChange={handleDebounceInputChange} closeOnSelect={false} open placeholder="Find your products" selectionBehavior="preserve" onValueChange={value => {
    const collection = value.items[0] as FiboCollectionComboboxItem;
    handleNavigate(collection?.url ?? '');
  }} positioning={{
    offset: {
      mainAxis: 4,
      crossAxis: 0
    }
  }} data-sentry-element="FibosearchCombobox.Root" data-sentry-component="HeaderComboboxSearchInput" data-sentry-source-file="HeaderComboboxSearchInput.tsx">
      <FibosearchCombobox.Control display="flex" width="100%" data-sentry-element="FibosearchCombobox.Control" data-sentry-source-file="HeaderComboboxSearchInput.tsx">
        <FibosearchCombobox.Input asChild data-sentry-element="FibosearchCombobox.Input" data-sentry-source-file="HeaderComboboxSearchInput.tsx">
          <HeaderInput ref={inputRef} onClick={handleInputClick} onFocus={handleInputFocus} onKeyDown={e => {
          if (e.key === 'Enter') {
            handleSearch();
          }
        }} data-sentry-element="HeaderInput" data-sentry-source-file="HeaderComboboxSearchInput.tsx" />
        </FibosearchCombobox.Input>
      </FibosearchCombobox.Control>

      <FibosearchCombobox.Positioner data-sentry-element="FibosearchCombobox.Positioner" data-sentry-source-file="HeaderComboboxSearchInput.tsx">
        <FibosearchCombobox.Content borderRadius="20px" data-sentry-element="FibosearchCombobox.Content" data-sentry-source-file="HeaderComboboxSearchInput.tsx">
          <Flex flexDirection="column" backgroundColor="white" paddingY="8px" paddingX="4px" ref={setComboboxContentRef} display={shouldDisplayContent ? 'block' : 'none'} className={CLASSNAME.NOT_TRANSLATE} data-sentry-element="Flex" data-sentry-source-file="HeaderComboboxSearchInput.tsx">
            <Condition expression={isGroupedCollection} then={<CollectionGroup groupedCollection={groupedCollection} inputValue={inputValue} onClose={() => setIsOpen(false)} />} else={<CollectionList collections={convertedCollectionComboboxItems} inputValue={inputValue} onClose={() => setIsOpen(false)} />} data-sentry-element="Condition" data-sentry-source-file="HeaderComboboxSearchInput.tsx" />
          </Flex>
        </FibosearchCombobox.Content>
      </FibosearchCombobox.Positioner>
    </FibosearchCombobox.Root>;
};
export default HeaderComboboxSearchInput;